/* En qué tema está el hub: "light" o "dark".
 *
 * ── PARA QUÉ HACE FALTA SABERLO EN JAVASCRIPT ───────────────────────────────
 *
 * Casi todo el hub resuelve el tema con CSS y no necesita esto. Los gráficos
 * no pueden: recharts y `react-activity-calendar` reciben los colores como
 * strings en props, y una `var(--…)` sin resolver no les sirve — la librería
 * del calendario directamente valida cada color con `CSS.supports` y descarta
 * lo que no entiende.
 *
 * Así que el tablero de Presidencia elige su paleta acá, en JS.
 *
 * ── EL MODO AUTOMÁTICO ES UN TERCER ESTADO, NO UN DEFAULT ───────────────────
 *
 * `data-theme` tiene tres situaciones y sólo dos valores:
 *
 *     data-theme="dark"    la persona eligió oscuro
 *     data-theme="light"   la persona eligió claro
 *     (sin atributo)       automático: manda el sistema operativo
 *
 * Leer sólo el atributo devuelve "light" para alguien en automático con el
 * sistema en oscuro, y ahí la pantalla se pinta para fondo blanco sobre fondo
 * negro. Por eso el tercer caso pregunta al sistema en vez de suponer.
 *
 * Es la misma lectura que ya hacía `PanelesFlotaBot` para avisarle el tema al
 * iframe de FlotaBot; estaba escrita allá adentro y ahora sale de acá, porque
 * dos copias de esta regla es cómo un día el panel y el tablero terminan
 * pintados distinto.
 */

import { useEffect, useState } from "react";

import { baseDeTema } from "./registroTemas";

export function temaActual() {
  if (typeof document === "undefined") return "light";
  const puesto = document.documentElement.getAttribute("data-theme");
  // `baseDeTema` mapea cada theme conocido a su modo claro/oscuro —incluidos los
  // que no se llaman "light"/"dark" (ej. "industrial")—. Si no hay base fija
  // (automático o desconocido), manda el sistema operativo.
  const base = baseDeTema(puesto);
  if (base) return base;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/* El tema, y se vuelve a dibujar cuando cambia.
 *
 * Escucha las DOS cosas: el atributo —que cambia cuando alguien toca el
 * selector de Preferencias— y el sistema, que cambia solo al anochecer si la
 * persona está en automático. Con una sola, la mitad de los cambios de tema
 * dejarían los gráficos con la paleta anterior hasta la próxima recarga.
 */
export function useTema() {
  const [tema, setTema] = useState(temaActual);

  useEffect(() => {
    const releer = () => setTema(temaActual());
    const obs = new MutationObserver(releer);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    mq?.addEventListener?.("change", releer);
    // Por si el tema cambió entre el primer render y este efecto.
    releer();
    return () => {
      obs.disconnect();
      mq?.removeEventListener?.("change", releer);
    };
  }, []);

  return tema;
}
